import React from 'react';
import { Box, IconButton, SxProps } from '@mui/material';
import { useSnapCarousel } from 'react-snap-carousel';
import { ChevronRight } from './icons/chevron-right';

type CarouselProps<T> = {
  items: T[];
  renderItem: (
    props: CarouselRenderItemProps<T>
  ) => React.ReactElement<CarouselItemProps>;
  sx?: SxProps;
};

type CarouselRenderItemProps<T> = {
  item: T;
  isSnapPoint: boolean;
};

export const Carousel = <T,>({ items, renderItem, sx }: CarouselProps<T>) => {
  const {
    scrollRef,
    hasPrevPage,
    hasNextPage,
    prev,
    next,
    snapPointIndexes,
    activePageIndex,
  } = useSnapCarousel();

  const arrowInset = '0.5rem';
  const arrowSx: SxProps = {
    position: 'absolute',
    top: 180,
    zIndex: 3,
    p: '1rem',
    fontSize: '0.5rem',
    color: 'primary.main',
  };

  return (
    <Box
      sx={{
        position: 'relative',
        mx: -3,
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        ...(sx || {}),
      }}
    >
      <Box
        sx={{
          position: 'relative',
          zIndex: 2,
          display: 'flex',
          overflow: 'auto',
          scrollSnapType: 'x mandatory',
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
          '&::-webkit-scrollbar': {
            display: 'none',
          },
        }}
        ref={scrollRef}
      >
        {items.map((item, i) =>
          renderItem({
            item,
            isSnapPoint: snapPointIndexes.has(i),
          })
        )}
      </Box>

      <IconButton
        sx={{
          left: arrowInset,
          ...arrowSx,
        }}
        onClick={() => prev()}
        disabled={!hasPrevPage}
      >
        <ChevronRight
          sx={{ transform: 'rotate(180deg)', fontSize: '0.75rem' }}
        />
      </IconButton>

      <IconButton
        sx={{
          right: arrowInset,
          ...arrowSx,
        }}
        onClick={() => next()}
        disabled={!hasNextPage}
      >
        <ChevronRight sx={{ fontSize: '0.75rem' }} />
      </IconButton>

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          gap: '6px',
          pt: { sm: 4 },
          mt: 'auto',
        }}
      >
        {items.map((_item, i) => {
          const active = i === activePageIndex;

          return (
            <Box
              sx={{
                height: '0.125rem',
                width: active ? '3.75rem' : '0.5rem',
                bgcolor: active ? 'primary.main' : 'grey.500',
                borderRadius: 1,
                transition: 'width 0.3s ease, background-color 0.3s ease',
              }}
              key={i}
            />
          );
        })}
      </Box>
    </Box>
  );
};

type CarouselItemProps = {
  isSnapPoint: boolean;
  children?: React.ReactNode;
};

export const CarouselItem = ({ isSnapPoint, children }: CarouselItemProps) => (
  <Box
    sx={{
      width: '100%',
      height: '100%',
      flexShrink: 0,
      scrollSnapAlign: isSnapPoint ? 'start' : 'none',
      px: 3,
    }}
  >
    {children}
  </Box>
);
